import React from "react";
import { useNavigate } from "react-router-dom";
import type {
  ILoginContext,
  IProps,
  SignInProps,
} from "../@types/context/loginContext.structure";
import loginService, { IConfirmaCodigoMfa } from "@services/Login/loginService";
import { datadogRum } from "@datadog/browser-rum";
import notifyServiceInstance from "@services/Notify/notifyService";
import chatServiceInstance from "@services/ChatBot/chatService";
import { useTitleAndMessageErrorToast } from "@hook/useMessageErrorToast";
import { messageToastLogin } from "src/pages/Login/Steps/CurrentLogin/messageToastLogin";
import { Flex, Text } from "@chakra-ui/react";

// Criação do contexto com um valor padrão vazio
export const LoginContext = React.createContext({} as ILoginContext);

// Hooks personalizados para acessar apenas partes específicas do contexto
export const useLoginAuth = () => {
  const context = React.useContext(LoginContext);
  return {
    user: context.user,
    isAuthenticated: context.isAuthenticated,
    confirmaCodigoMfa: context.confirmaCodigoMfa,
    signIn: context.signIn,
    signOut: context.signOut,
    errorLogin: context.errorLogin,
    setErrorLogin: context.setErrorLogin,
    loadingLogin: context.loadingLogin,
    isRemakeLogin: context.isRemakeLogin,
    setIsRemakeLogin: context.setIsRemakeLogin,
    accessChatBot: context.accessChatBot,
  };
};

export const useLoginNavigation = () => {
  const context = React.useContext(LoginContext);
  return {
    pageSidebar: context.pageSidebar,
    setPageSidebar: context.setPageSidebar,
    openSidebar: context.openSidebar,
    setOpenSidebar: context.setOpenSidebar,
    page: context.page,
    setPage: context.setPage,
    pageHelper: context.pageHelper,
    setPageHelper: context.setPageHelper,
    hashLogin: context.hashLogin,
    setHashLogin: context.setHashLogin,
  };
};

export const useLoginNotifications = () => {
  const context = React.useContext(LoginContext);
  return {
    notifications: context.notifications,
    setNotifications: context.setNotifications,
    isMarkedNotifications: context.isMarkedNotifications,
    setIsMarkedNotifications: context.setIsMarkedNotifications,
    // fetchNotifications: context.fetchNotifications,
  };
};

export const useLoginProfile = () => {
  const context = React.useContext(LoginContext);
  return {
    profile: context.profile,
    setProfile: context.setProfile,
    regional: context.regional,
    setRegional: context.setRegional,
    company: context.company,
    setCompany: context.setCompany,
    email: context.email,
  };
};

const LoginContextProvider: React.FC<IProps> = ({ children }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("@token");
  const displayMessageError = useTitleAndMessageErrorToast();
  const [user, setUser] = React.useState<boolean>(!!token);
  const isAuthenticated = user;
  const [profile, setProfile] = React.useState([]);
  const [regional, setRegional] = React.useState(
    localStorage.getItem("@regional"),
  );
  const [company, setCompany] = React.useState(
    localStorage.getItem("@empresa"),
  );
  const [errorLogin, setErrorLogin] = React.useState<boolean>(false);
  const [loadingLogin, setLoadingLogin] = React.useState<boolean>(false);
  const [openSidebar, setOpenSidebar] = React.useState<boolean>(true);
  const [errorForgotPassword, setErrorForgotPassword] =
    React.useState<boolean>(false);
  const [hashLogin, setHashLogin] = React.useState<string>("CurrentLogin");
  const [page, setPage] = React.useState<string>("");
  const [email, setEmail] = React.useState<string>(
    localStorage.getItem("@email") || "",
  );
  const [pageHelper, setPageHelper] = React.useState<string>("");
  const [pageSidebar, setPageSidebar] = React.useState<string>(
    localStorage.getItem("@option") || "Home",
  );

  const [isRemakeLogin, setIsRemakeLogin] = React.useState(false);
  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [isMarkedNotifications, setIsMarkedNotifications] =
    React.useState(false);
  const [accessChatBot, setAccessChatBot] = React.useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = React.useState(false);

  const [errorTitleLogin, setErrorTitleLogin] = React.useState<string>("");
  const [errorMessageLogin, setErrorMessageLogin] = React.useState<string>("");
  const [modalMultiAuth, setModalMultiAuth] = React.useState(false);
  const [session, setSession] = React.useState(
    localStorage.getItem("@session"),
  );
  const [flagEsqueceuASenha, setFlagEsqueceuASenha] = React.useState(false);

  // const fetchNotifications = React.useCallback(async () => {
  //   try {
  //     const response = await notifyServiceInstance.getNotify();

  //     if (response.status >= 400 && response?.status !== 401) {
  //       setNotifications([]);
  //       console.log("status 400", response);
  //       return;
  //     }
  //     if (response.status === 401) {
  //       setNotifications([]);
  //       return;
  //     }

  //     // Verificar se há alguma notificação com exibido === null
  //     const hasUnreadNotifications = Array.isArray(response)
  //       ? response.some((notification) => notification.exibido === null)
  //       : false;

  //     setIsMarkedNotifications(hasUnreadNotifications);

  //     setNotifications(Array.isArray(response) ? response : []);
  //   } catch (error) {
  //     console.log("Erro ao buscar notificações:", error);
  //     setNotifications([]);
  //   }
  // }, []);

  React.useEffect(() => {
    if (!isAuthenticated) return;

    const usuarioId = localStorage.getItem("@usuarioId");
    if (!usuarioId) return;

    const handleNovaNotificacao = (novaNotificacao: any) => {
      setNotifications((prev) => [novaNotificacao, ...prev]);
    };

    const handleListaNotificacoes = (listaAtualizada: any[]) => {
      setNotifications(listaAtualizada);
    };

    notifyServiceInstance.connect(
      usuarioId,
      handleNovaNotificacao,
      handleListaNotificacoes,
    );

    return () => {
      notifyServiceInstance.disconnect();
    };
  }, [isAuthenticated]);

  React.useEffect(() => {
    const hasUnread = notifications.some((notif) => notif.visualizado === null);

    setIsMarkedNotifications(hasUnread);
  }, [notifications, setIsMarkedNotifications]);

  // Verificar se o usuário já está logado ao carregar a página
  React.useEffect(() => {
    const storedUser = localStorage.getItem("@user");
    const storedRegional = localStorage.getItem("@regional");
    const storedCompany = localStorage.getItem("@empresa");
    const storedEmail = localStorage.getItem("@email");
    const storedPageSidebar = localStorage.getItem("@option");

    if (token && storedUser) {
      setUser(true);

      // Restaurar dados do usuário
      if (storedRegional) setRegional(storedRegional);
      if (storedCompany) setCompany(storedCompany);
      if (storedEmail) setEmail(storedEmail);
      if (storedPageSidebar) setPageSidebar(storedPageSidebar);

      // Buscar notificações se o usuário estiver logado
      // fetchNotifications();
    } else if (!token) {
      setUser(false);
    }
  }, [token]);

  const signIn = React.useCallback(
    async ({
      email,
      password,
      onClickForgotPassword = () => {},
      onClickFirstLogin = () => {},
    }: SignInProps) => {
      setLoadingLogin(true);
      try {
        const response = await loginService.postLogin({
          email,
          senha: password,
        });

        if (response === undefined) {
          console.log("errorLogin", response);
          setErrorLogin(true);
          setLoadingLogin(false);

          displayMessageError({
            title: response?.data?.titulo || "Erro",
            message: messageToastLogin(
              response?.data?.mensagem,
              () => {
                onClickFirstLogin();
                setFlagEsqueceuASenha(true);
              },
              () => {},
            ),
          });
          return;
        }

        if (response?.data?.deveRefazerPrimeiroAcesso) {
          setIsRemakeLogin(true);
          setLoadingLogin(false);

          displayMessageError({
            title: response?.data?.titulo,
            message: (
              <Flex direction="column" lineHeight={"5"}>
                Seus dados cadastrais foram atualizados no nosso sistema. Por
                isso, será necessário que você clique no link{" "}
                <Text
                  as="span"
                  color={"white"}
                  textDecoration="underline"
                  cursor="pointer"
                  w="fit-content"
                  onClick={() => {
                    onClickFirstLogin();
                    setFlagEsqueceuASenha(false);
                  }}
                >
                  Primeiro acesso.
                </Text>
              </Flex>
            ),
          });
          return;
        } else if (response?.status >= 400) {
          console.log("errorLogin", response);
          setErrorLogin(true);
          setLoadingLogin(false);

          displayMessageError({
            title: response?.data?.titulo,
            message: messageToastLogin(
              response?.data?.mensagem,
              () => {
                onClickFirstLogin();
                setFlagEsqueceuASenha(true);
              },
              () => {},
            ),
          });
          return;
        } else if (response?.session !== null) {
          setLoadingLogin(false);
          setModalMultiAuth(true);
          setSession(response?.session);
        } else {
          localStorage.setItem("@session", response?.session);
          setLoadingLogin(false);

          const { access_token_mfa } = response;

          const token = response?.authenticationResult.idToken;

          const refreshToken = response?.authenticationResult.refreshToken;
          const idTokenTemp = response?.authenticationResult.idToken;

          localStorage.setItem("@token", token);
          localStorage.setItem("@tokenNotification", idTokenTemp);
          localStorage.setItem("@accessTokenMfa", access_token_mfa);

          // Salvar refresh token se disponível
          if (refreshToken) {
            localStorage.setItem("@refreshToken", refreshToken);
          }

          localStorage.setItem("@atividade", response?.atividade);
          localStorage.setItem("@tipoDeUsuario", response?.tipoDeUsuario);
          localStorage.setItem("@idCorretor", response?.idCorretorSalesforce);
          localStorage.setItem("@usuarioId", response?.usuarioId);
          localStorage.setItem("@email", email);
          localStorage.setItem("@user", response?.nomeCorretor);
          localStorage.setItem("@regional", response?.regional);
          localStorage.setItem("@regiao", response?.regiao);
          localStorage.setItem("@empresa", response?.empresa);
          localStorage.setItem("@cpf", response?.cpf);
          localStorage.setItem("@dtNasc", response?.dataNascimento);
          localStorage.setItem("@option", "Home");
          setPageSidebar("Home");

          datadogRum.setUser({
            id: response?.idCorretorSalesforce,
            name: response?.nomeCorretor,
            email: email,
            regional: response?.regional,
            empresa: response?.empresa,
            regiao: response?.regiao,
            cpf: response?.cpf,
          });

          setRegional(response?.regional);
          setCompany(response?.empresa);
          setEmail(email);
          setUser(true);
          navigate("/");
          setLoadingLogin(false);
          // setErrorLogin(false);
          setPage("");
          setModalMultiAuth(false);
        }
      } catch (error) {
        setLoadingLogin(false);
        console.log("ERRO AO FAZER LOGIN ", error);
      }
    },
    [],
  );

  const confirmaCodigoMfa = React.useCallback(
    async ({ email, codigoVerificacao, session }: IConfirmaCodigoMfa) => {
      setLoadingLogin(true);
      try {
        const response = await loginService.confirmaCodigoMfa({
          email,
          codigoVerificacao,
          session,
        });

        if (response?.status >= 400) {
          console.log("errorMfa", response);
          // setErrorLogin(true);
          setLoadingLogin(false);

          displayMessageError({
            title: response?.data?.titulo,
            message: response?.data?.mensagem,
          });
          return;
        } else {
          const { access_token, access_token_mfa, idToken, refresh_token } =
            response;

          localStorage.setItem("@accessTokenMfa", access_token_mfa);
          localStorage.setItem("@token", access_token);
          localStorage.setItem("@tokenNotification", idToken);

          // Salvar refresh token se disponível
          if (refresh_token) {
            localStorage.setItem("@refreshToken", refresh_token);
          }

          localStorage.setItem("@atividade", response?.atividade);
          localStorage.setItem("@tipoDeUsuario", response?.tipoDeUsuario);
          localStorage.setItem("@idCorretor", response?.idCorretorSalesforce);
          localStorage.setItem("@usuarioId", response?.usuarioId);
          localStorage.setItem("@email", email);
          localStorage.setItem("@user", response?.nomeCorretor);
          localStorage.setItem("@regional", response?.regional);
          localStorage.setItem("@regiao", response?.regiao);
          localStorage.setItem("@empresa", response?.empresa);
          localStorage.setItem("@cpf", response?.cpf);
          localStorage.setItem("@dtNasc", response?.dataNascimento);
          localStorage.setItem("@option", "Home");
          setPageSidebar("Home");

          datadogRum.setUser({
            id: response?.idCorretorSalesforce,
            name: response?.nomeCorretor,
            email: email,
            regional: response?.regional,
            empresa: response?.empresa,
            regiao: response?.regiao,
            cpf: response?.cpf,
          });

          setRegional(response?.regional);
          setCompany(response?.empresa);
          setEmail(email);
          setUser(true);
          navigate("/");
          setLoadingLogin(false);
          // setErrorLogin(false);
          setPage("");
          setModalMultiAuth(false);
          // await fetchNotifications();
        }
      } catch (error) {
        setLoadingLogin(false);
        console.log("Erro no MFA ", error);
      }
    },
    [displayMessageError, navigate],
  );

  const refreshPage = React.useCallback(() => {
    setTimeout(() => {
      window.location.reload();
      localStorage.removeItem("@token");
      localStorage.removeItem("@refreshToken");
      localStorage.removeItem("@tokenNotification");
      localStorage.setItem("@option", "Criar oportunidade");
      localStorage.clear();
      datadogRum.clearUser();
    }, 500);
  }, []);

  const signOut = React.useCallback(() => {
    try {
      refreshPage();
    } catch {
      console.log("erro ao deslogar");
    }
  }, [refreshPage]);

  // Memoize o valor do contexto para evitar re-renderizações desnecessárias
  const contextValue = React.useMemo(
    () => ({
      flagEsqueceuASenha,
      setFlagEsqueceuASenha,
      errorTitleLogin,
      errorMessageLogin,
      modalMultiAuth,
      setModalMultiAuth,
      session,
      confirmaCodigoMfa,
      accessChatBot,
      setAccessChatBot,
      setIsRemakeLogin,
      // fetchNotifications,
      notifications,
      setNotifications,
      isMarkedNotifications,
      setIsMarkedNotifications,
      regional,
      setRegional,
      setCompany,
      company,
      hashLogin,
      setHashLogin,
      user,
      setUser,
      email,
      openSidebar,
      setOpenSidebar,
      isAuthenticated,
      page,
      setPage,
      pageHelper,
      setPageHelper,
      profile,
      setProfile,
      signIn,
      signOut,
      errorLogin,
      setErrorLogin,
      loadingLogin,
      errorForgotPassword,
      setErrorForgotPassword,
      pageSidebar,
      setPageSidebar,
      isRemakeLogin,
    }),
    [
      flagEsqueceuASenha,
      setFlagEsqueceuASenha,
      errorTitleLogin,
      errorMessageLogin,
      modalMultiAuth,
      setModalMultiAuth,
      session,
      confirmaCodigoMfa,
      accessChatBot,
      setAccessChatBot,
      setIsRemakeLogin,
      // fetchNotifications,
      notifications,
      setNotifications,
      isMarkedNotifications,
      setIsMarkedNotifications,
      regional,
      setRegional,
      setCompany,
      company,
      hashLogin,
      setHashLogin,
      user,
      setUser,
      email,
      openSidebar,
      setOpenSidebar,
      isAuthenticated,
      page,
      setPage,
      pageHelper,
      setPageHelper,
      profile,
      setProfile,
      signIn,
      signOut,
      errorLogin,
      setErrorLogin,
      loadingLogin,
      errorForgotPassword,
      setErrorForgotPassword,
      pageSidebar,
      setPageSidebar,
      isRemakeLogin,
    ],
  );

  React.useEffect(() => {
    const checkAccessChatBot = async () => {
      const regional = localStorage.getItem("@regional") || "";
      setAccessChatBot(false);
      setIsCheckingAccess(true);
      try {
        const response = await chatServiceInstance.getAccessChatBot(regional);
        setAccessChatBot(response); // true ou false
      } catch (error) {
        console.error("Erro ao verificar acesso ao chatbot:", error);
        setAccessChatBot(false);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAccessChatBot();
  }, [setAccessChatBot]);

  return (
    <LoginContext.Provider value={contextValue}>
      {children}
    </LoginContext.Provider>
  );
};

export default React.memo(LoginContextProvider);
